import { useMemo } from 'react';

interface ForensicStatsProps {
  returns: number[];
  benchmarkReturns?: number[];
}

interface StatCard {
  label: string;
  value: string;
  color: 'green' | 'red' | 'neutral' | 'na';
}

const pearsonCorrelation = (x: number[], y: number[]): number => {
  const n = x.length;
  if (n < 3) return 0;
  const mx = x.reduce((s, v) => s + v, 0) / n;
  const my = y.reduce((s, v) => s + v, 0) / n;
  let num = 0, dx2 = 0, dy2 = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i] - mx;
    const dy = y[i] - my;
    num += dx * dy;
    dx2 += dx * dx;
    dy2 += dy * dy;
  }
  const denom = Math.sqrt(dx2 * dy2);
  return denom === 0 ? 0 : num / denom;
};

export default function ForensicStats({ returns, benchmarkReturns }: ForensicStatsProps) {
  const stats = useMemo((): StatCard[] => {
    if (returns.length < 2) return [];

    const n = returns.length;
    const mean = returns.reduce((s, r) => s + r, 0) / n;
    const variance = returns.reduce((s, r) => s + (r - mean) ** 2, 0) / (n - 1);
    const std = Math.sqrt(variance);
    const annualizedVol = std * Math.sqrt(252);

    // Extremes
    const best = Math.max(...returns);
    const worst = Math.min(...returns);

    // Win/loss breakdown
    const wins = returns.filter(r => r > 0);
    const losses = returns.filter(r => r < 0);
    const winRatio = wins.length / n;
    const avgWin = wins.length > 0 ? wins.reduce((s, r) => s + r, 0) / wins.length : 0;
    const avgLoss = losses.length > 0 ? losses.reduce((s, r) => s + r, 0) / losses.length : 0;

    // Consecutive streaks
    let maxConsecWins = 0, maxConsecLosses = 0;
    let curWins = 0, curLosses = 0;
    for (const r of returns) {
      if (r > 0) { curWins++; curLosses = 0; }
      else if (r < 0) { curLosses++; curWins = 0; }
      else { curWins = 0; curLosses = 0; }
      maxConsecWins = Math.max(maxConsecWins, curWins);
      maxConsecLosses = Math.max(maxConsecLosses, curLosses);
    }

    // Lag-1 autocorrelation
    const autoCorr = pearsonCorrelation(returns.slice(0, -1), returns.slice(1));

    // Up/down capture vs benchmark
    let upCapture: number | null = null;
    let downCapture: number | null = null;
    if (benchmarkReturns && benchmarkReturns.length === returns.length) {
      const upDays: number[] = [];
      const upStrat: number[] = [];
      const downDays: number[] = [];
      const downStrat: number[] = [];
      for (let i = 0; i < n; i++) {
        if (benchmarkReturns[i] > 0) {
          upDays.push(benchmarkReturns[i]);
          upStrat.push(returns[i]);
        } else if (benchmarkReturns[i] < 0) {
          downDays.push(benchmarkReturns[i]);
          downStrat.push(returns[i]);
        }
      }
      if (upDays.length > 0) {
        const bmUp = upDays.reduce((s, r) => s + r, 0) / upDays.length;
        const stUp = upStrat.reduce((s, r) => s + r, 0) / upStrat.length;
        upCapture = bmUp !== 0 ? (stUp / bmUp) * 100 : null;
      }
      if (downDays.length > 0) {
        const bmDown = downDays.reduce((s, r) => s + r, 0) / downDays.length;
        const stDown = downStrat.reduce((s, r) => s + r, 0) / downDays.length;
        downCapture = bmDown !== 0 ? (stDown / bmDown) * 100 : null;
      }
    }

    const cards: StatCard[] = [
      {
        label: 'Best Day',
        value: `+${(best * 100).toFixed(2)}%`,
        color: 'green'
      },
      {
        label: 'Worst Day',
        value: `${(worst * 100).toFixed(2)}%`,
        color: 'red'
      },
      {
        label: 'Annualized Vol',
        value: `${(annualizedVol * 100).toFixed(1)}%`,
        color: 'neutral'
      },
      {
        label: 'Up Capture',
        value: upCapture !== null ? `${upCapture.toFixed(0)}%` : 'N/A',
        color: upCapture !== null && upCapture > 100 ? 'green' : upCapture !== null ? 'neutral' : 'na'
      },
      {
        label: 'Down Capture',
        value: downCapture !== null ? `${downCapture.toFixed(0)}%` : 'N/A',
        color: downCapture !== null && downCapture < 100 ? 'green' : downCapture !== null ? 'red' : 'na'
      },
      {
        label: 'Autocorrelation (lag-1)',
        value: autoCorr.toFixed(3),
        color: Math.abs(autoCorr) < 0.05 ? 'neutral' : autoCorr > 0 ? 'green' : 'red'
      },
      {
        label: 'Positive Days',
        value: `${wins.length} (${(winRatio * 100).toFixed(1)}%)`,
        color: winRatio > 0.5 ? 'green' : 'neutral'
      },
      {
        label: 'Negative Days',
        value: `${losses.length} (${((losses.length / n) * 100).toFixed(1)}%)`,
        color: 'neutral'
      },
      {
        label: 'Avg Win',
        value: `+${(avgWin * 100).toFixed(2)}%`,
        color: 'green'
      },
      {
        label: 'Avg Loss',
        value: `${(avgLoss * 100).toFixed(2)}%`,
        color: 'red'
      },
      {
        label: 'Max Consec. Wins',
        value: `${maxConsecWins}`,
        color: 'neutral'
      },
      {
        label: 'Max Consec. Losses',
        value: `${maxConsecLosses}`,
        color: 'neutral'
      }
    ];

    return cards;
  }, [returns, benchmarkReturns]);

  if (stats.length === 0) return null;

  const colorClass = (c: StatCard['color']): string => {
    switch (c) {
      case 'green': return 'text-emerald-400';
      case 'red': return 'text-orange-400';
      case 'na': return 'text-gray-600';
      default: return 'text-gray-200';
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
      <h3 className="text-sm font-medium text-gray-400 mb-3">Forensic Statistics</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-gray-800/50 rounded-md p-3">
            <p className="text-xs text-gray-500 mb-1">{s.label}</p>
            <p className={`text-lg font-semibold tabular-nums ${colorClass(s.color)}`}>
              {s.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
